// src/components/ScrollToTop/ScrollToTop.tsx
// ─────────────────────────────────────────────────────────────────────────
// Global "back to top" button. Mounted once in AppLayout so it covers every
// route with no per-page wiring. The app scrolls on the window (the content
// area has no inner overflow container), so a single window-scroll listener
// is correct here.
//
// Behavior (product decision):
//   • Appears once the user has scrolled past one viewport height.
//   • Smooth scroll to top on click, unless the user prefers reduced motion.
//   • Fixed bottom-right; hidden from assistive tech until it's actionable.
// ─────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useState } from 'react';
import styles from './ScrollToTop.module.scss';

const ScrollToTop: React.FC = () => {
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		// Threshold: one viewport. Recomputed on each scroll so it adapts to
		// resize/orientation changes without a separate resize listener.
		const onScroll = () => {
			setVisible(window.scrollY > window.innerHeight);
		};
		onScroll(); // sync initial state (e.g. on a route that restores scroll)
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	const handleClick = useCallback(() => {
		const prefersReduced = window.matchMedia(
			'(prefers-reduced-motion: reduce)',
		).matches;
		window.scrollTo({
			top: 0,
			behavior: prefersReduced ? 'auto' : 'smooth',
		});
	}, []);

	if (!visible) return null;

	return (
		<button
			type='button'
			className={styles.button}
			onClick={handleClick}
			aria-label='Scroll to top'>
			<svg
				viewBox='0 0 24 24'
				fill='none'
				stroke='currentColor'
				strokeWidth='2.2'
				strokeLinecap='round'
				strokeLinejoin='round'
				aria-hidden='true'>
				<path d='M12 19V5M5 12l7-7 7 7' />
			</svg>
		</button>
	);
};

export default ScrollToTop;
